import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Button } from "reactstrap";
import { DatosSesion } from "../../Components/Common/DatosSesion";
import { guardarLayoutDashboard } from "../../helpers/backend_helper";
import { useLayoutCache, invalidateLayoutCache } from "./useLayoutCache";

// Crear un contexto para compartir isEditMode con valor por defecto más seguro
export const DashboardEditContext = React.createContext(null);

// Hook personalizado para consumir el contexto de forma segura
export const useDashboardEdit = () => {
  const context = React.useContext(DashboardEditContext);
  if (context === null) {
    // Retornar valores por defecto si el contexto no está disponible
    return { isEditMode: false };
  }
  return context;
};

const DraggableDashboard = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  // Usar hook compartido para cargar layout
  const { layout: cachedLayout, loading: layoutLoading } = useLayoutCache('DraggableDashboard');

  // Definir IDs para cada sección del dashboard
  // Orden por defecto especificado por el usuario
  const sectionIds = [
    'solicitudes',    // 1. Solicitudes Activas, Próximo Mes, Históricas (3 cards)
    'comparativa',    // 2. Comparativa por Proveedor (solo Admin)
    'promedio',       // 3. Promedio por Factura
    'kpis',           // 4. Widget con KPIs principales
    'diagnosticos',   // 5. Diagnósticos Farmacológicos (Top 10)
    'motorizados'     // 6. Rendimiento de Motorizados (Top 10)
  ];

  // Función para migrar IDs antiguos a nuevos
  const migrateLayout = (layout) => {
    const migrations = {
      'solicitudes-promedio': ['solicitudes', 'promedio'] // Dividir en 2
    };
    
    let migratedLayout = [];
    layout.forEach(item => {
      if (migrations[item.id]) {
        // Reemplazar con los nuevos IDs
        migrations[item.id].forEach(newId => {
          migratedLayout.push({ id: newId, order: migratedLayout.length });
        });
      } else if (sectionIds.includes(item.id)) {
        // Mantener si el ID es válido
        migratedLayout.push({ ...item, order: migratedLayout.length });
      }
    });
    
    // Agregar IDs faltantes al final
    sectionIds.forEach(id => {
      if (!migratedLayout.find(item => item.id === id)) {
        migratedLayout.push({ id, order: migratedLayout.length });
      }
    });
    
    return migratedLayout;
  };

  // Procesar layout cacheado cuando esté disponible
  useEffect(() => {
    if (!layoutLoading) {
      if (cachedLayout) {
        // Migrar layout si tiene IDs antiguos
        const migratedLayout = migrateLayout(cachedLayout);
        //console.log("DraggableDashboard - Layout procesado desde caché:", migratedLayout);
        setItems(migratedLayout);
      } else {
        // Usar layout por defecto si no hay nada guardado
        //console.log("DraggableDashboard - Usando layout por defecto");
        setItems(sectionIds.map((id, index) => ({ id, order: index })));
      }
    }
  }, [cachedLayout, layoutLoading]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index
    }));

    setItems(updatedItems);
  };

  const saveLayout = async () => {
    setSaving(true);
    try {
      const farmacia = DatosSesion("farmacia");
      const id = DatosSesion("id");
      const correo = DatosSesion("correo");
      const identificador = farmacia || id || correo || "default";
      const localStorageKey = `dashboard_layout_${identificador}`;
      
      // Guardar en localStorage primero (siempre funciona)
      localStorage.setItem(localStorageKey, JSON.stringify(items));
      
      // Intentar guardar en el servidor también
      try {
        await guardarLayoutDashboard(identificador, items);
        //console.log("Layout guardado en servidor y localStorage");
        
        // Invalidar caché para que otros componentes recarguen
        invalidateLayoutCache();
      } catch (serverError) {
        // Si falla el servidor, no importa porque ya está en localStorage
        if (serverError.response?.status !== 404) {
          console.warn("No se pudo guardar en el servidor, pero se guardó localmente:", serverError);
        }
      }
      
      setIsEditMode(false);
      
      // Mostrar mensaje de éxito temporal
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000); // Ocultar después de 3 segundos
      
      //console.log("✅ Layout del dashboard guardado exitosamente");
    } catch (error) {
      console.error("Error al guardar layout:", error);
      // Solo mostrar alerta si hay error
      alert("❌ Error al guardar el layout. Se guardó localmente.");
    } finally {
      setSaving(false);
    }
  };

  const resetLayout = () => {
    setItems(sectionIds.map((id, index) => ({ id, order: index })));
  };

  if (items.length === 0) {
    return <>{children}</>;
  }

  const childrenArray = React.Children.toArray(children);
  
  //console.log("DraggableDashboard - Items order:", items.map(i => i.id));
  //console.log("DraggableDashboard - Section IDs:", sectionIds);
  //console.log("DraggableDashboard - Total children:", childrenArray.length);
   /* childrenArray.forEach((child, index) => {
   console.log(`Child ${index}:`, {
      type: child?.type?.name || child?.type,
      props: child?.props,
      hasChildren: !!child?.props?.children
    });
  }); */
  
  // Función para verificar si un componente debe mostrarse
  const shouldShowComponent = (child) => {
    // Si el child es null, undefined, false, o un string vacío
    if (!child) return false;
    
    // Si es un Fragment vacío
    if (child.type === React.Fragment && !child.props.children) return false;
    
    // Si el child tiene props.children y es null (renderizado condicional)
    if (React.isValidElement(child) && child.props && child.props.children === null) return false;
    
    // Verificar si es un Row con children null
    if (child.type && child.type.name === 'Row' && !child.props.children) return false;
    
    return true;
  };

  return (
    <DashboardEditContext.Provider value={{ isEditMode }}>
      {/* Mensaje de éxito */}
      {showSuccessMessage && (
        <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
          <i className="ri-checkbox-circle-line me-2"></i>
          <strong>¡Guardado!</strong> Tu configuración del dashboard se ha guardado exitosamente.
        </div>
      )}
      
      {/* Botones de control */}
      <div className="d-flex justify-content-end mb-3">
        {!isEditMode ? (
          <Button
            color="primary"
            size="sm"
            onClick={() => setIsEditMode(true)}
            className="shadow-sm"
          >
            <i className="ri-layout-grid-line me-1"></i>
            Personalizar Dashboard
          </Button>
        ) : (
          <div className="d-flex gap-2">
            <Button
              color="success"
              size="sm"
              onClick={saveLayout}
              disabled={saving}
            >
              <i className="ri-save-line me-1"></i>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button
              color="secondary"
              size="sm"
              outline
              onClick={resetLayout}
            >
              <i className="ri-refresh-line me-1"></i>
              Restablecer
            </Button>
            <Button
              color="danger"
              size="sm"
              outline
              onClick={() => setIsEditMode(false)}
            >
              <i className="ri-close-line me-1"></i>
              Cancelar
            </Button>
          </div>
        )}
      </div>

      {isEditMode ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="dashboard-sections">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={snapshot.isDraggingOver ? "bg-light p-2 rounded" : ""}
              >
                {items.map((item, index) => {
                  // Buscar el child que corresponde a este item.id
                  const childIndex = sectionIds.indexOf(item.id);
                  const child = childrenArray[childIndex];
                  const isVisible = shouldShowComponent(child);
                  
                  /*console.log(`Item ${index} (${item.id}):`, {
                    childIndex,
                    hasChild: !!child,
                    isVisible
                  });*/
                  
                  return (
                    <Draggable key={item.id} draggableId={item.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            marginBottom: '1.5rem',
                          }}
                          className={`${snapshot.isDragging ? 'shadow-lg' : ''}`}
                        >
                          <div 
                            className="position-relative border rounded p-3 bg-white"
                            style={{
                              opacity: snapshot.isDragging ? 0.8 : isVisible ? 1 : 0.5,
                            }}
                          >
                            {/* Handle de arrastre */}
                            <div
                              {...provided.dragHandleProps}
                              className="position-absolute top-0 start-50 translate-middle-x"
                              style={{ 
                                zIndex: 1000, 
                                cursor: 'grab',
                                marginTop: '-10px',
                                backgroundColor: isVisible ? '#405189' : '#878a99',
                                borderRadius: '50%',
                                padding: '8px 12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                              }}
                            >
                              <i className="ri-drag-move-2-fill text-white fs-18"></i>
                            </div>
                            {isVisible ? (
                              child
                            ) : (
                              <div className="text-center py-4">
                                <i className="ri-eye-off-line fs-1 text-muted"></i>
                                <p className="text-muted mt-2 mb-0">
                                  <small>
                                    {item.id === 'diagnosticos' && 'Diagnósticos Farmacológicos (requiere fechas)'}
                                    {item.id === 'motorizados' && 'Rendimiento de Motorizados (requiere fechas)'}
                                    {item.id === 'comparativa' && 'Comparativa de Proveedores (requiere fechas y perfil Admin)'}
                                    {!['diagnosticos', 'motorizados', 'comparativa'].includes(item.id) && 'Sección no disponible'}
                                  </small>
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <>
          {items.map((item, index) => {
            const childIndex = sectionIds.indexOf(item.id);
            const child = childrenArray[childIndex];
            const isVisible = shouldShowComponent(child);
            
            /*console.log(`Rendering item ${index} (${item.id}):`, {
              childIndex,
              hasChild: !!child,
              isVisible,
              willRender: isVisible
            });*/
            
            // Solo renderizar si el componente está visible
            if (!isVisible) {
              console.warn(`Skipping item ${item.id} - not visible`);
              return null;
            }
            
            return <React.Fragment key={item.id}>{child}</React.Fragment>;
          })}
        </>
      )}
    </DashboardEditContext.Provider>
  );
};

export default DraggableDashboard;
